/**
 * Tournées — regroupement de passages pour une journée / zone / équipe (LOT 1).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AlertTriangle, ChevronRight, Play, RefreshCw, Square } from "lucide-react";
import { api } from "../../../lib/axios";
import {
  completeTourneeExecution,
  notifyAdminExecutionPassagesRefresh,
  startTourneeExecution,
} from "../../../modules/execution/services/execution.service";
import { mapExecutionApiError } from "../../../modules/execution/services/executionMutations";
import {
  TOURNEE_OPERATIONAL_MODE_OPTIONS,
  TourneeOperationalMode,
  tourneeModeAllowsOptionalFleetVehicle,
} from "@/domain/planning/tourneeOperationalMode";

function toYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Lundi–dimanche (locale), inclusif. */
function weekBoundsLocal(base: Date): { from: string; to: string } {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const dow = d.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: toYmdLocal(monday), to: toYmdLocal(sunday) };
}

function dedupePassagesById<T extends { id: number }>(rows: T[]): T[] {
  const m = new Map<number, T>();
  for (const r of rows) {
    if (!m.has(r.id)) m.set(r.id, r);
  }
  return [...m.values()];
}

type TourneeRow = {
  id: number;
  numero: string | null;
  date: string;
  statut: string;
  /** Exécution workflow (aligné Prisma) — prioritaire sur `statut` pour les actions. */
  executionStatus?: string;
  zone?: { id: number; nom: string } | null;
  equipe?: {
    id: number;
    nom: string;
    capaciteJour?: number | null;
    zoneId?: number | null;
    actif?: boolean;
  } | null;
  vehicule?: { id: number; immatriculation: string } | null;
  chauffeur?: { id: number; nom: string; prenom: string } | null;
  passages?: Array<{
    id: number;
    client?: { id: number; nom?: string | null; prenom?: string | null; nomEntreprise?: string | null } | null;
  }>;
  _count?: { passages: number };
};

function clientNameFromTourneePassage(
  client?: { id: number; nom?: string | null; prenom?: string | null; nomEntreprise?: string | null } | null
): string {
  if (!client) return "Client non renseigné";
  return (
    client.nomEntreprise?.trim() ||
    [client.prenom, client.nom].filter(Boolean).join(" ").trim() ||
    `Client #${client.id}`
  );
}

function tourneeClientLabel(t: TourneeRow): string {
  const labels = Array.from(
    new Set((t.passages ?? []).map((p) => clientNameFromTourneePassage(p.client)).filter(Boolean))
  );
  if (labels.length === 0) return "—";
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

/** Exécution terrain (Prisma `executionStatus`) — source de vérité pour Lancer / Terminer. */
function tourneeExecutionStatus(t: TourneeRow): string {
  if (t.executionStatus) return t.executionStatus;
  if (t.statut === "TERMINEE") return "TERMINEE";
  if (t.statut === "EN_COURS") return "EN_COURS";
  return "PLANIFIEE";
}

function tourneeExecutionLabel(ex: string): string {
  switch (ex) {
    case "PLANIFIEE":
      return "Planifiée";
    case "EN_COURS":
      return "En cours";
    case "SUSPENDUE":
      return "Suspendue";
    case "TERMINEE":
      return "Terminée";
    default:
      return ex;
  }
}

type OptionZone = { id: number; nom: string };
type OptionEquipe = { id: number; nom: string; capaciteJour?: number | null; zoneId?: number | null };
type OptionVehicule = { id: number; immatriculation: string };
type PassageOption = {
  id: number;
  datePrevue: string;
  statut: string;
  passageType?: string | null;
  isUrgent?: boolean;
  tournee?: { id: number; numero: string | null } | null;
  zone?: { id: number; nom: string } | null;
  client?: { id: number; nom?: string | null; prenom?: string | null; nomEntreprise?: string | null } | null;
};

type TourneeHealth = {
  progressPercent: number;
  completedPassages: number;
  totalPassages: number;
  pendingPassages: number;
  capacityMax: number | null;
  capacityUsed: number;
  capacityRemaining: number | null;
  overload: boolean;
  lateCount: number;
  urgentCount: number;
  slaRiskCount: number;
  healthStatus: "OK" | "WARNING" | "CRITICAL";
};

export default function TourneesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [dateTo, setDateTo] = useState(todayIso);
  const [zoneId, setZoneId] = useState("");
  const [statutF, setStatutF] = useState("");
  const [items, setItems] = useState<TourneeRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [nDate, setNDate] = useState(todayIso);
  const [nZone, setNZone] = useState("");
  const [modeGlobal, setModeGlobal] = useState(false);
  const [nEquipe, setNEquipe] = useState("");
  const [nVehicule, setNVehicule] = useState("");
  const [nModeOperationnel, setNModeOperationnel] = useState<TourneeOperationalMode>(TourneeOperationalMode.WALKING);
  const [zones, setZones] = useState<OptionZone[]>([]);
  const [equipes, setEquipes] = useState<OptionEquipe[]>([]);
  const [vehicules, setVehicules] = useState<OptionVehicule[]>([]);
  const [dispoZone, setDispoZone] = useState("");
  const [dispoPriority, setDispoPriority] = useState("ALL");
  const [passagesDispo, setPassagesDispo] = useState<PassageOption[]>([]);
  const [selectedPassages, setSelectedPassages] = useState<number[]>([]);
  const [healthByTourneeId, setHealthByTourneeId] = useState<Record<number, TourneeHealth>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { from: dateFrom, to: dateTo, limit: "100", page: "1" };
      if (zoneId.trim()) params.zoneId = zoneId.trim();
      if (statutF.trim()) params.statut = statutF.trim();
      const res = await api.get("/tournees", { params });
      const rows = (res.data?.items ?? []) as TourneeRow[];
      setItems(rows);
      const healthEntries = await Promise.all(
        rows.map(async (t) => {
          try {
            const hRes = await api.get(`/tournees/${t.id}/health`);
            return [t.id, hRes.data?.health as TourneeHealth] as const;
          } catch {
            return [t.id, null] as const;
          }
        })
      );
      const next: Record<number, TourneeHealth> = {};
      healthEntries.forEach(([id, h]) => {
        if (h) next[id] = h;
      });
      setHealthByTourneeId(next);
    } catch {
      toast.error("Impossible de charger les tournées.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, statutF, zoneId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Anciens liens ?tourneeId= → fiche détail canonique */
  useEffect(() => {
    const raw = searchParams.get("tourneeId");
    if (!raw || !/^\d+$/.test(raw.trim())) return;
    const id = Number(raw.trim());
    if (id > 0) {
      navigate(`/admin/planificateur/tournees/${id}`, { replace: true });
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    Promise.all([
      api.get("/zones").catch(() => ({ data: [] })),
      api.get("/equipes").catch(() => ({ data: [] })),
      api.get("/charrois/vehicules").catch(() => ({ data: [] })),
    ]).then(([zRes, eRes, vRes]) => {
      setZones(Array.isArray(zRes.data) ? (zRes.data as OptionZone[]) : []);
      setEquipes(Array.isArray(eRes.data) ? (eRes.data as OptionEquipe[]) : []);
      const vehs = Array.isArray(vRes.data) ? (vRes.data as Array<{ id: number; immatriculation?: string }>) : [];
      setVehicules(vehs.map((v) => ({ id: v.id, immatriculation: v.immatriculation ?? `Véhicule #${v.id}` })));
      const firstZoneId =
        Array.isArray(zRes.data) && zRes.data.length > 0 ? String((zRes.data as OptionZone[])[0].id) : "";
      if (firstZoneId) {
        setZoneId((prev) => prev || firstZoneId);
        setNZone((prev) => prev || firstZoneId);
        setDispoZone((prev) => prev || firstZoneId);
      }
    });
  }, []);

  const loadPassagesDisponibles = useCallback(async () => {
    try {
      const params: Record<string, string> = {
        from: dateFrom,
        to: dateTo,
        limit: "300",
        page: "1",
        planifTourneesDisponibles: "true",
      };
      if (dispoZone.trim()) params.zoneId = dispoZone.trim();
      const res = await api.get("/passages", { params });
      let rows = dedupePassagesById((res.data?.items ?? []) as PassageOption[]).filter((p) => !p.tournee);
      if (dispoPriority === "URGENT") rows = rows.filter((p) => p.isUrgent);
      if (dispoPriority === "NORMALE") rows = rows.filter((p) => !p.isUrgent);
      setPassagesDispo(rows);
      setSelectedPassages((prev) => prev.filter((id) => rows.some((p) => p.id === id)));
    } catch {
      setPassagesDispo([]);
    }
  }, [dateFrom, dateTo, dispoPriority, dispoZone]);

  useEffect(() => {
    void loadPassagesDisponibles();
  }, [loadPassagesDisponibles]);

  const selectedEquipe = useMemo(
    () => equipes.find((e) => String(e.id) === nEquipe.trim()) ?? null,
    [equipes, nEquipe]
  );

  const kpis = useMemo(() => {
    const now = Date.now();
    const passagesNonAffectes = passagesDispo.length;
    const urgences = passagesDispo.filter((p) => p.isUrgent).length;
    const retards = passagesDispo.filter((p) => new Date(p.datePrevue).getTime() < now).length;
    const tourneesActives = items.filter((t) => {
      const ex = tourneeExecutionStatus(t);
      return ex === "EN_COURS" || ex === "SUSPENDUE";
    }).length;
    const equipesDisponibles = equipes.filter((e) => (e.capaciteJour ?? 0) > 0).length;
    const capaciteRestante = items.reduce((acc, t) => {
      const cap = t.equipe?.capaciteJour ?? 0;
      const loadCount = t._count?.passages ?? 0;
      return acc + Math.max(0, cap - loadCount);
    }, 0);
    return { passagesNonAffectes, urgences, retards, tourneesActives, equipesDisponibles, capaciteRestante };
  }, [equipes, items, passagesDispo]);

  const dispatchContext = useMemo(
    () => ({
      hotspots: passagesDispo.filter((p) => p.isUrgent).map((p) => p.zone?.nom ?? "Sans zone"),
      candidateTeams: equipes
        .filter((e) => !nZone || !e.zoneId || String(e.zoneId) === nZone)
        .map((e) => ({ id: e.id, nom: e.nom, capacite: e.capaciteJour ?? 0 })),
    }),
    [equipes, nZone, passagesDispo]
  );

  const createTournee = async () => {
    try {
      if (selectedPassages.length === 0) {
        toast.warning("Sélectionnez au moins un passage.");
        return;
      }
      if (!modeGlobal && !nZone.trim()) {
        toast.warning("Zone obligatoire sauf en mode global.");
        return;
      }
      const body: Record<string, unknown> = { date: `${nDate}T00:00:00.000Z`, modeOperationnel: nModeOperationnel };
      if (!modeGlobal && nZone.trim()) body.zoneId = Number(nZone);
      if (nEquipe.trim()) body.equipeId = Number(nEquipe);
      if (tourneeModeAllowsOptionalFleetVehicle(nModeOperationnel) && nVehicule.trim()) {
        body.vehiculeId = Number(nVehicule);
      }
      const res = await api.post("/tournees", body);
      const tId = Number(res.data?.tournee?.id);
      if (Number.isInteger(tId) && tId > 0 && selectedPassages.length > 0) {
        await Promise.all(
          selectedPassages.map((pid) => api.post(`/passages/${pid}/assign-tournee`, { tourneeId: tId }))
        );
      }
      toast.success(
        `Tournée créée #${res.data?.tournee?.id ?? ""}${selectedPassages.length > 0 ? ` (${selectedPassages.length} passage(s) ajoutés)` : ""}`
      );
      setSelectedPassages([]);
      void load();
      void loadPassagesDisponibles();
    } catch {
      toast.error("Création impossible.");
    }
  };

  const startTn = async (id: number) => {
    try {
      await startTourneeExecution(id);
      toast.success("Tournée lancée");
      notifyAdminExecutionPassagesRefresh();
      void load();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      if (err.response?.data?.error === "TOURNEE_ALREADY_STARTED") {
        toast.info("Cette tournée est déjà en cours.");
        void load();
        return;
      }
      toast.error(mapExecutionApiError(e, "Impossible de lancer la tournée."));
    }
  };

  const finishTn = async (id: number) => {
    try {
      const out = await completeTourneeExecution(id);
      const msg =
        out.closeWarnings?.length > 0
          ? `Tournée clôturée — ${out.closeWarnings.join("; ")}`
          : "Tournée clôturée";
      toast.success(msg);
      void load();
    } catch (e) {
      toast.error(mapExecutionApiError(e, "Impossible de clôturer la tournée."));
    }
  };

  return (
    <div className="space-y-6 p-4 text-slate-100 md:p-6">
      <header className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/30 p-5 shadow-xl md:p-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/90">Exécution</p>
        <h1 className="mt-1 text-2xl font-bold text-white md:text-3xl">Tournées</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">
          Regroupement opérationnel des passages. L’historique consolidé des passages se trouve sous Terrain →
          Historique passages.
        </p>
        <Link
          to="/admin/planificateur/passages"
          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Passages
          <ChevronRight className="h-4 w-4" />
        </Link>
        <Link
          to="/admin/tournees/analytics"
          className="mt-4 ml-2 inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Analyse tournées
          <ChevronRight className="h-4 w-4" />
        </Link>
      </header>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <KpiTile label="Passages non affectés" value={String(kpis.passagesNonAffectes)} tone="amber" />
        <KpiTile label="Urgences" value={String(kpis.urgences)} tone="rose" />
        <KpiTile label="Retards" value={String(kpis.retards)} tone="yellow" />
        <KpiTile label="Tournées actives" value={String(kpis.tourneesActives)} tone="emerald" />
        <KpiTile label="Équipes disponibles" value={String(kpis.equipesDisponibles)} tone="sky" />
        <KpiTile label="Capacité restante" value={String(kpis.capaciteRestante)} tone="slate" />
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Période</h2>
        <p className="mt-1 text-xs text-slate-400">
          Filtre commun aux passages disponibles et à la liste des tournées (plage inclusive).
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Date début
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={(e) => {
                const v = e.target.value;
                setDateFrom(v);
                if (v > dateTo) setDateTo(v);
              }}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Date fin
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              onChange={(e) => {
                const v = e.target.value;
                setDateTo(v);
                if (v < dateFrom) setDateFrom(v);
              }}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <button
            type="button"
            onClick={() => {
              const { from, to } = weekBoundsLocal(new Date());
              setDateFrom(from);
              setDateTo(to);
              setNDate(from);
            }}
            className="rounded-xl border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
          >
            Cette semaine
          </button>
          <button
            type="button"
            onClick={() => {
              setDateFrom(todayIso);
              setDateTo(todayIso);
              setNDate(todayIso);
            }}
            className="rounded-xl border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
          >
            Aujourd&apos;hui
          </button>
          <button
            type="button"
            onClick={() => {
              setDateFrom(todayIso);
              setDateTo(todayIso);
              setNDate(todayIso);
              setStatutF("");
              setDispoPriority("ALL");
              setZoneId("");
              setDispoZone("");
            }}
            className="rounded-xl border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
          >
            Réinitialiser
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="text-lg font-semibold text-white">Passages à affecter</h2>
        <p className="mt-1 text-xs text-slate-400">
          Centre opérationnel pour affecter les passages, puis créer la tournée adaptée.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Date de la tournée
            <input
              type="date"
              value={nDate}
              onChange={(e) => setNDate(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Zone
            <select
              value={nZone}
              onChange={(e) => setNZone(e.target.value)}
              className="min-w-40 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              disabled={modeGlobal}
            >
              <option value="">Sélectionner une zone</option>
              {zones.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-300">
            <input type="checkbox" checked={modeGlobal} onChange={(e) => setModeGlobal(e.target.checked)} />
            Mode global (toutes zones)
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Équipe
            <select
              value={nEquipe}
              onChange={(e) => setNEquipe(e.target.value)}
              className="min-w-44 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Aucune</option>
              {equipes
                .filter((e) => !nZone || !e.zoneId || String(e.zoneId) === nZone)
                .map((e) => (
                  <option key={e.id} value={String(e.id)}>
                    {e.nom}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Mode opérationnel
            <select
              value={nModeOperationnel}
              onChange={(e) => setNModeOperationnel(e.target.value as TourneeOperationalMode)}
              className="min-w-44 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              {TOURNEE_OPERATIONAL_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {tourneeModeAllowsOptionalFleetVehicle(nModeOperationnel) ? (
            <label className="flex flex-col gap-1 text-xs text-slate-400">
            Véhicule (optionnel)
            <select
              value={nVehicule}
              onChange={(e) => setNVehicule(e.target.value)}
              className="min-w-44 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Aucun véhicule rattaché</option>
              {vehicules.map((v) => (
                <option key={v.id} value={String(v.id)}>
                  {v.immatriculation}
                </option>
              ))}
            </select>
          </label>
          ) : null}
          <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs text-slate-300">
            Capacité équipe : {selectedEquipe?.capaciteJour ?? "—"} · Passages sélectionnés : {selectedPassages.length}
          </div>
          <button
            type="button"
            disabled={selectedPassages.length === 0}
            onClick={() => void createTournee()}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Créer tournée
          </button>
          {selectedPassages.length === 0 ? (
            <p className="text-xs text-amber-200">Sélectionnez au moins un passage.</p>
          ) : null}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-white">Passages disponibles pour cette tournée</h2>
          <button
            type="button"
            onClick={() => void loadPassagesDisponibles()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Période : du {new Date(`${dateFrom}T12:00:00`).toLocaleDateString("fr-FR")} au{" "}
          {new Date(`${dateTo}T12:00:00`).toLocaleDateString("fr-FR")} (modifiable dans « Période » ci-dessus).
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Zone
            <select
              value={dispoZone}
              onChange={(e) => setDispoZone(e.target.value)}
              className="min-w-40 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Toutes zones</option>
              {zones.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Priorité
            <select
              value={dispoPriority}
              onChange={(e) => setDispoPriority(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="ALL">Toutes</option>
              <option value="URGENT">Urgent</option>
              <option value="NORMALE">Normale / faible</option>
            </select>
          </label>
        </div>
        <div className="mt-4 max-h-64 overflow-y-auto rounded-xl border border-slate-800">
          {passagesDispo.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-slate-400">
              <div className="mb-2 inline-flex items-center gap-2 text-emerald-300">
                <AlertTriangle className="h-4 w-4" />
                État opérationnel
              </div>
              <p>Tout est affecté pour ce filtre.</p>
              <p className="mt-1 text-xs text-slate-500">
                Aucune urgence à traiter immédiatement. Essayez une autre date/zone ou créez une tournée globale.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-800 text-sm">
              {passagesDispo.map((p) => {
                const label =
                  p.client?.nomEntreprise ||
                  [p.client?.nom, p.client?.prenom].filter(Boolean).join(" ") ||
                  `Client #${p.client?.id ?? "—"}`;
                const checked = selectedPassages.includes(p.id);
                return (
                  <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <label className="flex items-center gap-2 text-slate-200">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setSelectedPassages((prev) =>
                            e.target.checked ? [...prev, p.id] : prev.filter((x) => x !== p.id)
                          )
                        }
                      />
                      <span>{label}</span>
                    </label>
                    <div className="text-xs text-slate-400">
                      {p.passageType === "PREMIERE_INTERVENTION" ? "Première intervention" : "Standard"} ·{" "}
                      {p.isUrgent ? "URGENT" : "NORMALE"} · {new Date(p.datePrevue).toLocaleString("fr-FR")} ·{" "}
                      {p.zone?.nom ?? "Sans zone"}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-white">Tournées (filtres)</h2>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Zone
            <select
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
              className="min-w-40 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Sélectionner zone</option>
              {zones.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Statut
            <select
              value={statutF}
              onChange={(e) => setStatutF(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Tous</option>
              <option value="BROUILLON">BROUILLON</option>
              <option value="PLANIFIEE">PLANIFIEE</option>
              <option value="EN_COURS">EN_COURS</option>
              <option value="TERMINEE">TERMINEE</option>
              <option value="ANNULEE">ANNULEE</option>
            </select>
          </label>
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-3">#</th>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Équipe</th>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Passages</th>
                <th className="px-3 py-3">Charge</th>
                <th className="px-3 py-3">Cap. restante</th>
                <th className="px-3 py-3">Progression</th>
                <th className="px-3 py-3">Santé</th>
                <th className="px-3 py-3">Retard</th>
                <th className="px-3 py-3">SLA Risk</th>
                <th className="px-3 py-3">SLA</th>
                <th className="px-3 py-3">Retard</th>
                <th className="px-3 py-3">Statut</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/90">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-3 py-10 text-center text-slate-500">
                    {loading ? "Chargement…" : "Aucune tournée."}
                  </td>
                </tr>
              ) : (
                items.map((t) => {
                  const ex = tourneeExecutionStatus(t);
                  return (
                  <tr key={t.id} className="bg-slate-900/30">
                    <td className="px-3 py-2 font-mono text-emerald-300">
                      {t.numero ?? `#${t.id}`}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      {new Date(t.date).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      <div>{t.equipe?.nom ?? "—"}</div>
                      {t.equipe?.capaciteJour != null && (
                        <div className="text-xs text-slate-500">
                          Cap. jour : {t.equipe.capaciteJour}
                        </div>
                      )}
                      {t._count && t.equipe?.capaciteJour != null && t.equipe.capaciteJour > 0 && t._count.passages > t.equipe.capaciteJour && (
                        <div className="mt-1 text-xs font-medium text-amber-400">
                          Passages &gt; capacité ({t._count.passages}/{t.equipe.capaciteJour})
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      <span title={tourneeClientLabel(t)}>{tourneeClientLabel(t)}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-300">{t._count?.passages ?? 0}</td>
                    <td className="px-3 py-2 text-slate-300">
                      {t.equipe?.capaciteJour && t.equipe.capaciteJour > 0
                        ? `${Math.min(100, Math.round(((t._count?.passages ?? 0) / t.equipe.capaciteJour) * 100))}%`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      {t.equipe?.capaciteJour && t.equipe.capaciteJour > 0
                        ? Math.max(0, t.equipe.capaciteJour - (t._count?.passages ?? 0))
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      {healthByTourneeId[t.id]
                        ? `${healthByTourneeId[t.id].progressPercent}% — ${healthByTourneeId[t.id].completedPassages}/${healthByTourneeId[t.id].totalPassages}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {healthByTourneeId[t.id] ? (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            healthByTourneeId[t.id].healthStatus === "OK"
                              ? "bg-emerald-500/20 text-emerald-200"
                              : healthByTourneeId[t.id].healthStatus === "WARNING"
                                ? "bg-amber-500/20 text-amber-200"
                                : "bg-rose-500/20 text-rose-200"
                          }`}
                        >
                          {healthByTourneeId[t.id].healthStatus}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-300">{healthByTourneeId[t.id]?.lateCount ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-300">{healthByTourneeId[t.id]?.slaRiskCount ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-300">
                      {ex === "TERMINEE" ? "Respecté" : ex === "EN_COURS" || ex === "SUSPENDUE" ? "En suivi" : "Planifié"}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      {new Date(t.date).getTime() < Date.now() && ex !== "TERMINEE" ? "Oui" : "Non"}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-lg bg-slate-800 px-2 py-0.5 text-xs font-semibold text-amber-200">
                        {tourneeExecutionLabel(ex)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={ex !== "PLANIFIEE"}
                          title={
                            ex === "PLANIFIEE"
                              ? "Lancer la tournée (tous les passages planifiés passent en exécution)"
                              : ex === "TERMINEE"
                                ? "Tournée terminée"
                                : "Tournée déjà lancée — utilisez Exécution passages pour les passages"
                          }
                          onClick={() => {
                            if (ex === "PLANIFIEE") void startTn(t.id);
                          }}
                          className="rounded-lg border border-emerald-600/50 p-1.5 text-emerald-400 hover:bg-emerald-950/50 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <Play className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={ex !== "EN_COURS"}
                          title={
                            ex === "EN_COURS"
                              ? "Clôturer la tournée (équivalent fin d’exécution — aucun passage en cours)"
                              : "Clôture disponible uniquement quand la tournée est en cours"
                          }
                          onClick={() => {
                            if (ex === "EN_COURS") void finishTn(t.id);
                          }}
                          className="rounded-lg border border-amber-600/50 p-1.5 text-amber-300 hover:bg-amber-950/40 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <Square className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/admin/planificateur/tournees/${t.id}`}
                          title="Voir détails"
                          className="rounded-lg border border-sky-600/50 px-2 py-1 text-xs text-sky-300 hover:bg-sky-950/40"
                        >
                          Détails
                        </Link>
                        <Link
                          to="/admin/execution-passages"
                          title="Suivi exécution passages"
                          className="rounded-lg border border-violet-600/50 px-2 py-1 text-xs text-violet-300 hover:bg-violet-950/40"
                        >
                          Suivi
                        </Link>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          Préparation dispatch intelligent : structure prête pour suggestions auto (équipe/tournée) basée zone + capacité.
          {dispatchContext.hotspots.length > 0 ? ` Hotspots: ${dispatchContext.hotspots.slice(0, 3).join(", ")}.` : ""}
        </p>
      </section>
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "amber" | "rose" | "yellow" | "emerald" | "sky" | "slate";
}) {
  const toneClass =
    tone === "amber"
      ? "text-amber-200 border-amber-500/30"
      : tone === "rose"
        ? "text-rose-200 border-rose-500/30"
        : tone === "yellow"
          ? "text-yellow-200 border-yellow-500/30"
          : tone === "emerald"
            ? "text-emerald-200 border-emerald-500/30"
            : tone === "sky"
              ? "text-sky-200 border-sky-500/30"
              : "text-slate-200 border-slate-700";
  return (
    <article className={`rounded-2xl border bg-slate-900/40 p-3 ${toneClass}`}>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </article>
  );
}
